import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Fuse from "fuse.js";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

import Layout from "../components/Layout";
import Breadcrumb from "../components/Breadcrumb";
import SearchBar from "../components/SearchBar";
import IndustryIcon from "../components/IndustryIcon";
import { CardSkeleton } from "../components/Skeletons";
import { getIndustry } from "../lib/api";
import NotFoundPage from "./NotFoundPage";

export default function IndustryPage() {
  const { industrySlug } = useParams();
  const [industry, setIndustry] = useState(null);
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setNotFound(false);
    getIndustry(industrySlug)
      .then((d) => {
        if (!mounted) return;
        setIndustry(d.industry);
        setProblems(d.problems || []);
      })
      .catch((e) => {
        if (!mounted) return;
        if (e?.response?.status === 404) setNotFound(true);
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [industrySlug]);

  const fuse = useMemo(
    () => new Fuse(problems, { keys: ["title"], threshold: 0.32, ignoreLocation: true }),
    [problems]
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return problems;
    return fuse.search(query).map((r) => r.item);
  }, [query, fuse, problems]);

  if (notFound) return <NotFoundPage />;

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-12 pb-6">
        <Breadcrumb
          items={[
            { label: "Directory", to: "/" },
            { label: industry?.name || "…" },
          ]}
        />
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-10 border-b border-ink-200">
        {loading || !industry ? (
          <div className="border-l border-ink-200 pl-8 py-2 space-y-4">
            <div className="skeleton h-3 w-24" />
            <div className="skeleton h-10 w-2/3" />
            <div className="skeleton h-4 w-full max-w-xl" />
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="grid md:grid-cols-12 gap-8"
          >
            <div className="md:col-span-2 flex md:justify-end pt-1">
              <div className="inline-flex p-3 bg-ink-100 text-ink-900 rounded-sm border border-ink-200">
                <IndustryIcon name={industry.icon} size={22} />
              </div>
            </div>
            <div className="md:col-span-10">
              <p className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500">
                IND.{String(industry.id).padStart(2, "0")} · {problems.length} documented problems
              </p>
              <h1
                className="mt-3 font-heading text-3xl md:text-4xl lg:text-5xl font-medium text-ink-900 tracking-[-0.02em] leading-[1.1]"
                data-testid="industry-title"
              >
                {industry.name}
              </h1>
              <p className="mt-4 max-w-3xl text-base text-ink-600 leading-relaxed">
                {industry.description}
              </p>
            </div>
          </motion.div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 pb-24">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500">
              Section 02 · Problem index
            </p>
            <h2 className="mt-2 font-heading text-xl md:text-2xl font-medium text-ink-900 tracking-tight">
              Select a documented problem.
            </h2>
          </div>
          <div className="w-full max-w-md">
            <SearchBar
              value={query}
              onChange={setQuery}
              placeholder="Filter problems in this vertical…"
              testId="industry-search"
            />
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink-200 border border-ink-200">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-bone">
                <CardSkeleton />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="border border-ink-200 bg-bone p-12 text-center"
            data-testid="industry-empty"
          >
            <p className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500">
              No results
            </p>
            <p className="mt-3 font-heading text-xl text-ink-900">
              No problems match &ldquo;{query}&rdquo;
            </p>
            <p className="mt-2 text-sm text-ink-500">Try a broader search term.</p>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink-200 border border-ink-200"
            data-testid="problems-grid"
          >
            {filtered.map((p, idx) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: Math.min(idx * 0.01, 0.25) }}
                className="bg-bone"
              >
                <Link
                  to={`/${industrySlug}/${p.slug}`}
                  className="group flex h-full p-6 md:p-7 transition-colors duration-200 hover:bg-ink-50"
                  data-testid={`problem-card-${p.slug}`}
                >
                  <span className="font-mono text-[10px] text-ink-400 mr-5 mt-1 tabular-nums tracking-wide-2 uppercase shrink-0">
                    {String(p.problem_number).padStart(2, "0")}
                  </span>
                  <div className="flex-1">
                    <h3 className="font-heading text-base md:text-lg font-medium text-ink-900 leading-snug tracking-tight">
                      {p.title}
                    </h3>
                    <div className="mt-5 flex items-center justify-between">
                      <span className="font-mono text-[10px] uppercase tracking-wide-2 text-ink-500">
                        View recommendation
                      </span>
                      <ArrowRight
                        size={14}
                        className="text-ink-400 group-hover:text-ink-900 group-hover:translate-x-1 transition-all"
                      />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}
